import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import toast from 'react-hot-toast'
import { GridColDef } from '@mui/x-data-grid'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Switch, Typography } from '@mui/material'
import translate from '../../i18n/translate'
import { OtpBypassItem, userApi } from '../../modules/services/userService'
import OtpPhonesPage, { formatOtpDate, OtpUserCell, useOtpListState } from '../UI/OtpPhonesPage'
import OtpBypassModal from './OtpBypassModal'

const OtpBypassScreen = () => {
  const intl = useIntl()
  const listState = useOtpListState()

  const [isAddOpen, setIsAddOpen] = useState(false)
  const [itemToRemove, setItemToRemove] = useState<OtpBypassItem | null>(null)

  const { data, isFetching } = userApi.useGetOtpBypassListQuery(listState.query)
  const [setActive] = userApi.useSetOtpBypassActiveMutation()
  const [removePhone, { isLoading: isRemoving }] = userApi.useRemoveOtpBypassPhoneMutation()

  const onActiveChange = async (id: number, isActive: boolean) => {
    try {
      await setActive({ id, isActive }).unwrap()
    } catch {
      toast.error(intl.formatMessage({ id: 'otpBypassUpdateFailed' }))
    }
  }

  const onRemoveConfirm = async () => {
    if (!itemToRemove) return

    try {
      await removePhone(itemToRemove.id).unwrap()
      toast.success(intl.formatMessage({ id: 'otpBypassRemoveSuccess' }))
    } catch {
      toast.error(intl.formatMessage({ id: 'otpBypassRemoveFailed' }))
    } finally {
      setItemToRemove(null)
    }
  }

  const columns = useMemo<GridColDef<OtpBypassItem>[]>(() => [
    {
      field: 'phone',
      flex: 1,
      sortable: false,
      headerName: intl.formatMessage({ id: 'otpListPhone' }),
    },
    {
      field: 'user',
      flex: 1,
      sortable: false,
      headerName: intl.formatMessage({ id: 'otpListUser' }),
      renderCell: ({ row }) => <OtpUserCell user={row.user} />,
    },
    {
      field: 'createdAt',
      flex: 1,
      sortable: false,
      headerName: intl.formatMessage({ id: 'otpBypassAddedAt' }),
      renderCell: ({ row }) => formatOtpDate(row.createdAt),
    },
    {
      field: 'isActive',
      flex: 1,
      sortable: false,
      headerName: intl.formatMessage({ id: 'otpBypassActive' }),
      renderCell: ({ row }) => (
        <Switch color="warning" checked={row.isActive} onChange={(e) => onActiveChange(row.id, e.target.checked)} />
      ),
    },
    {
      field: 'actions',
      flex: 1,
      sortable: false,
      headerName: intl.formatMessage({ id: 'otpListActions' }),
      renderCell: ({ row }) => (
        <Button color="warning" onClick={() => setItemToRemove(row)}>
          {translate('otpBypassRemove')}
        </Button>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [intl])

  return (
    <OtpPhonesPage
      title={translate('otpBypassScreenTitle')}
      addLabel={translate('otpBypassAdd')}
      onAdd={() => setIsAddOpen(true)}
      listState={listState}
      rows={data?.list || []}
      count={data?.count || 0}
      columns={columns}
      loading={isFetching}
    >
      {isAddOpen && <OtpBypassModal onClose={() => setIsAddOpen(false)} />}

      <Dialog open={!!itemToRemove} onClose={() => setItemToRemove(null)}>
        <DialogTitle>{translate('otpBypassRemove')}</DialogTitle>
        <DialogContent>
          <Typography>{intl.formatMessage({ id: 'otpBypassRemoveConfirm' }, { phone: itemToRemove?.phone })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setItemToRemove(null)}>
            {translate('cancel')}
          </Button>
          <Button color="warning" variant="contained" onClick={onRemoveConfirm} disabled={isRemoving}>
            {translate('otpBypassRemove')}
          </Button>
        </DialogActions>
      </Dialog>
    </OtpPhonesPage>
  )
}

export default OtpBypassScreen
